import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Database, Download, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { downloadBackupFile, exportBackup } from "@/lib/backup/v1/exportBackup";
import { toast } from "@/lib/safeToast";

export function DataPersistenceBanner() {
  const { user } = useAuth();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [lastExport, setLastExport] = useState<string | null>(() => localStorage.getItem("psn:last_export"));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      const tables = ["patients", "substances", "clinical_sessions", "medication_dose_logs", "session_checkins"] as const;
      const res: Record<string, number> = {};
      for (const table of tables) {
        const { count } = await (supabase as any).from(table).select("id", { count: "exact", head: true });
        res[table] = count ?? 0;
      }
      if (live) setCounts(res);
    })();
    return () => { live = false; };
  }, [user?.id]);

  async function doQuickExport() {
    if (!user) return;
    setBusy(true);
    try {
      const backup = await exportBackup(user.id, {
        kind: "full",
        includePharmacology: true,
        includeDerived: true,
        includeAuditHistory: false,
      });
      downloadBackupFile(backup);
      localStorage.setItem("psn:last_export", backup.exportedAt);
      setLastExport(backup.exportedAt);
      toast.success("Backup ClinicalOS v1 gerado com checksum SHA-256.");
    } catch (error: any) {
      toast.error(error?.message ?? "Falha ao exportar.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Database className="h-4 w-4 text-primary" /> Persistência de dados
        </div>
        <Badge variant="outline" className="text-[10px] flex items-center gap-1">
          <ShieldCheck className="h-3 w-3" /> Backup v1
        </Badge>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        <div className="space-y-1">
          <div><span className="text-muted-foreground">Conta:</span> <b>{user?.email ?? "—"}</b></div>
          <div className="text-[10px] text-muted-foreground">
            Exportação exclui tokens, senhas e segredos. Restaurações têm prévia e não apagam registros automaticamente.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(counts).map(([key, value]) => (
            <div key={key} className="rounded-md border border-border/60 px-2 py-1">
              <div className="text-[10px] text-muted-foreground">{key.replace(/_/g, " ")}</div>
              <div className="font-mono text-sm">{value}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <AlertTriangle className="h-3 w-3" />
        Último backup local: {lastExport ? new Date(lastExport).toLocaleString("pt-BR") : "nunca"}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={doQuickExport} disabled={busy}><Download className="h-4 w-4 mr-1.5" />Backup rápido</Button>
        <Button asChild variant="outline">
          <Link to="/backup"><SlidersHorizontal className="h-4 w-4 mr-1.5" />Backup e restauração</Link>
        </Button>
      </div>
    </Card>
  );
}
